'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	BN,
	BigNum,
	QUOTE_PRECISION_EXP,
	QUOTE_SPOT_MARKET_INDEX,
	ZERO,
} from '@velocity-exchange/sdk';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import Button from '../primitives/Button/Button';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import ResponsiveModal from './ResponsiveModal/ResponsiveModal';
import useModal from './hooks/useModal';
import { useIsolatedPositionMargin } from 'src/hooks/positions/useIsolatedPositionMargin';
import { useTransferIsolatedPositionMargin } from 'src/hooks/positions/useTransferIsolatedPositionMargin';
import useDriftClient from 'src/hooks/useDriftClient';
import TextButton from '../primitives/TextButton/TextButton';
import TradeInput from '../Inputs/TradeInput';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';

interface Props {
	marketIndex: number;
}

export default function PerpIsolatedPositionMarginModal({
	marketIndex,
}: Props) {
	const { hide } = useModal();
	const driftClient = useDriftClient();
	const transferIsolatedPositionMargin = useTransferIsolatedPositionMargin();
	const currentAccount = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']
	);
	const userAccountClient = currentAccount?.client;

	const position = useMemo(() => {
		return userAccountClient?.getPerpPosition(marketIndex);
	}, [userAccountClient, marketIndex]);

	const [amountInput, setAmountInput] = useState('');
	const [tab, setTab] = useState<'add' | 'remove'>('add');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isWithdrawMax, setIsWithdrawMax] = useState(false);

	const tabOptions = !position?.baseAssetAmount.eq(ZERO)
		? [
				{
					value: 'add' as const,
					label: 'Add Margin',
				},
				{
					value: 'remove' as const,
					label: 'Remove Margin',
				},
			]
		: [
				{
					value: 'remove' as const,
					label: 'Remove Margin',
				},
			];

	const currentMargin = useIsolatedPositionMargin(marketIndex);

	// NEW: required initial margin and maximum removable
	const requiredInitialMargin = useMemo(() => {
		if (!userAccountClient || !position)
			return BigNum.zero(QUOTE_PRECISION_EXP);
		const marginCalculation = userAccountClient.getMarginCalculation('Initial');
		const isolatedMarginCalculation =
			marginCalculation.isolatedMarginCalculations.get(marketIndex);
		return BigNum.from(
			isolatedMarginCalculation?.marginRequirementPlusBuffer,
			QUOTE_PRECISION_EXP
		);
	}, [userAccountClient, position, marketIndex]);

	const maxRemovable = useMemo(() => {
		if (position?.baseAssetAmount.eq(ZERO)) {
			return currentMargin;
		}

		return BigNum.max(
			BigNum.zero(QUOTE_PRECISION_EXP),
			currentMargin
				.sub(requiredInitialMargin)
				.mul(new BN(980))
				.div(new BN(1000)) // 2% buffer to avoid insufficient collateral error
		);
	}, [currentMargin, requiredInitialMargin]);

	const quoteBalance = useMemo(() => {
		const user = driftClient?.hasUser() ? driftClient.getUser() : undefined;
		const amt = user ? user.getTokenAmount(QUOTE_SPOT_MARKET_INDEX) : new BN(0);
		return BigNum.from(amt, QUOTE_PRECISION_EXP);
	}, [driftClient]);

	const canSubmit = useMemo(() => {
		if (!driftClient || !currentAccount) return false;
		if (!amountInput) return false;
		try {
			const parsed = BigNum.fromPrint(amountInput, QUOTE_PRECISION_EXP);
			if (!isWithdrawMax && !parsed.gt(BigNum.zero(QUOTE_PRECISION_EXP)))
				return false;
			if (tab === 'add') {
				return parsed.lte(quoteBalance);
			}
			return isWithdrawMax || parsed.lte(maxRemovable);
		} catch (_e) {
			return false;
		}
	}, [
		driftClient,
		currentAccount,
		amountInput,
		tab,
		maxRemovable,
		isWithdrawMax,
		quoteBalance,
	]);

	const maxAvailableLabel = useMemo(() => {
		return tab === 'add'
			? quoteBalance.toNotional(true)
			: maxRemovable.toNotional(true);
	}, [tab, quoteBalance, maxRemovable]);

	const amountBigNum = useMemo(() => {
		return amountInput
			? BigNum.fromPrint(amountInput, QUOTE_PRECISION_EXP)
			: BigNum.zero(QUOTE_PRECISION_EXP);
	}, [amountInput]);

	const liqPrice = useMemo(() => {
		if (!userAccountClient || !position) return ZERO;
		return position?.baseAssetAmount.gt(ZERO)
			? userAccountClient?.liquidationPrice(
					marketIndex,
					ZERO,
					ZERO,
					'Maintenance',
					false,
					tab === 'add' ? amountBigNum.val : amountBigNum.val.neg(),
					'Isolated'
				)
			: ZERO;
	}, [
		userAccountClient,
		marketIndex,
		amountBigNum,
		tab,
		position?.baseAssetAmount,
	]);

	const newMargin = useMemo(() => {
		return tab === 'add'
			? currentMargin.add(amountBigNum)
			: BigNum.max(
					BigNum.zero(QUOTE_PRECISION_EXP),
					currentMargin.sub(amountBigNum)
				);
	}, [tab, currentMargin, amountBigNum]);

	const onConfirm = useCallback(async () => {
		const parsed = BigNum.fromPrint(amountInput, QUOTE_PRECISION_EXP);
		if (!isWithdrawMax && parsed.lte(BigNum.zero(QUOTE_PRECISION_EXP))) return;

		const isFullWithdrawal =
			isWithdrawMax ||
			(parsed.eq(maxRemovable) && currentMargin.sub(parsed).eq(ZERO));

		setIsSubmitting(true);
		try {
			await transferIsolatedPositionMargin(
				`${tab}_isolated_margin_${marketIndex}_${Date.now()}`,
				{
					amount: parsed,
					marketIndex,
					isAdd: tab === 'add',
					isFullWithdrawal,
					positionBaseAssetAmount: position?.baseAssetAmount ?? ZERO,
				}
			);
			hide();
		} finally {
			setIsSubmitting(false);
		}
	}, [
		amountInput,
		marketIndex,
		hide,
		tab,
		isWithdrawMax,
		maxRemovable,
		currentMargin,
		transferIsolatedPositionMargin,
		position?.baseAssetAmount,
	]);

	function sanitizeAmountInput(value: string): string {
		const cleaned = value.replace(/[^\d.]/g, '');
		const hadDot = cleaned.includes('.');
		const endsWithDot = cleaned.endsWith('.');
		const [integerPart, ...rest] = cleaned.split('.');
		const decimalPart = rest.join('');
		let result =
			integerPart + (decimalPart.length > 0 ? '.' + decimalPart : '');
		if (hadDot && endsWithDot && decimalPart.length === 0) {
			result = (integerPart || '0') + '.';
		}
		if (result.startsWith('.') && result !== '') {
			result = '0' + result;
		}
		return result;
	}

	const handleAmountChange = useCallback(
		(value: string) => {
			const sanitized = sanitizeAmountInput(value);
			setAmountInput(sanitized);
			setIsWithdrawMax(false);
		},
		[setIsWithdrawMax]
	);

	const handleMaxClick = useCallback(() => {
		if (tab === 'add') {
			handleAmountChange(
				quoteBalance.prettyPrint(true, undefined).replace(/,/g, '')
			);
		} else {
			handleAmountChange(
				maxRemovable.prettyPrint(true, undefined).replace(/,/g, '')
			);
			setIsWithdrawMax(true);
		}
	}, [handleAmountChange, quoteBalance, maxRemovable, tab]);

	const suffix = useMemo(() => {
		return (
			<TextButton size="md" variant="tertiary" onClick={handleMaxClick}>
				Max
			</TextButton>
		);
	}, [handleMaxClick, quoteBalance, maxRemovable, tab]);

	useEffect(() => {
		if (isWithdrawMax) {
			handleAmountChange(
				maxRemovable.prettyPrint(true, undefined).replace(/,/g, '')
			);
			setIsWithdrawMax(true);
		}
	}, [isWithdrawMax, maxRemovable]);

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="isolated-position-margin-modal"
		>
			<ResponsiveModal.Title
				tabs={tabOptions}
				activeTab={tab}
				onTabChange={(nextTab) => {
					setTab(nextTab);
					if (tab !== nextTab) {
						setAmountInput('');
					}
					if (nextTab === 'add') {
						setIsWithdrawMax(false);
					}
				}}
				closeButtonTestId="isolated-position-margin-modal-close"
			>
				Adjust Margin
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<div className="flex flex-col gap-y-md">
					<div className="flex items-center gap-x-xs">
						<span>Max Available:</span>
						<span className="text-text-default">{maxAvailableLabel}</span>
					</div>

					<TradeInput
						id="isolated-position-margin-amount-input"
						label="Amount"
						value={amountInput}
						onChange={handleAmountChange}
						suffix={suffix}
						error={
							tab === 'add' && amountBigNum.gt(quoteBalance)
								? 'Exceeds your USDC balance.'
								: tab === 'remove' && amountBigNum.gt(maxRemovable)
									? 'Exceeds the max available amount.'
									: undefined
						}
					/>
				</div>

				<div className="flex flex-col gap-y-sm text-text-default">
					<div className="flex items-center justify-between">
						<span className="text-text-secondary">Current Margin</span>
						<NumberDisplay value={currentMargin} type="currency" />
					</div>
					<div className="flex items-center justify-between">
						<span className="text-text-secondary">New Margin</span>
						<NumberDisplay value={newMargin} type="currency" />
					</div>
					<div className="flex items-center justify-between">
						<span className="text-text-secondary">
							Est. Liq. Price after increase
						</span>
						{liqPrice.gt(ZERO) ? (
							<NumberDisplay
								value={BigNum.from(liqPrice, QUOTE_PRECISION_EXP)}
								type="currency"
							/>
						) : (
							PRICE_FALLBACK_DISPLAY_VALUE
						)}
					</div>
				</div>
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					className="w-full"
					onClick={onConfirm}
					disabled={!canSubmit}
					isLoading={isSubmitting}
					dataTestId="isolated-position-margin-modal-submit"
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
